<template>
  <section id="works" class="common-padding mb-20">
    <div class="flex flex-col">
      <h1
        id="selectedWorks"
        v-html="selectedWorks"
        class="heading-1 text-start font-bold uppercase leading-none"
      ></h1>
      <p
        class="heading-1 hidden w-4/5 text-end font-extrabold text-flax-smoke-400 text-opacity-50 sm:block"
      >
        ( {{ selectedWorksProps.length }} )
      </p>

      <div
        id="selected-works-text"
        class="mt-[5%] grid grid-cols-1 justify-end gap-4 text-flax-smoke-300 opacity-0 sm:grid-cols-12 md:column-gap lg:grid"
      >
        <p
          class="heading-6 col-span-full text-center text-flax-smoke-300/85 sm:col-span-4 sm:text-nowrap lg:col-start-2"
        >
          (
          <span class="inline sm:hidden">{{ selectedWorksProps.length }} </span>
          PROJECTS )
        </p>
        <p
          class="heading-5 font-fancy col-span-full w-full text-balance sm:heading-4 sm:col-span-8 sm:font-semibold lg:col-span-7"
        >
          Featured projects that have been meticulously crafted with
          passion and purpose over the years.
        </p>
      </div>
    </div>

    <div
      class="sm:column-gap relative mt-12 grid size-full grid-cols-12 lg:mt-[10%]"
    >
      <div
        class="sticky top-12 col-span-5 hidden h-fit w-full overflow-hidden text-[22vw] font-semibold leading-[0.8] text-flax-smoke-100 md:flex"
      >
        <span class="relative !font-title -tracking-wider">0</span>
        <span
          id="index"
          class="relative !font-title -tracking-wider will-change-transform"
          >{{ index + 1 }}.</span
        >
      </div>
      <aside
        @mouseenter="showCursor"
        @mouseleave="hideCursor"
        class="relative col-span-full flex flex-col space-y-6 sm:space-y-10 md:col-span-7"
      >
        <div
          v-for="(work, i) in selectedWorksProps"
          :key="i"
          class="@container work-card"
        >
          <a class="group" target="_blank" :href="work.url">
            <div
              class="flex-center relative aspect-square overflow-clip rounded-lg"
            >
              <img
                alt="work-background"
                loading="lazy"
                class="absolute size-full select-none object-cover"
                :src="work.imageBg"
              />
              <div
                class="flex-center z-10 aspect-[4/3] size-full overflow-clip rounded-lg object-cover"
              >


                <video
                  :data-src="work.videoSrc"  
                  loop
                  muted
                  @error="handleVideoError"
                  type="video/mp4"
                  class="work-video size-[80%] rounded-md object-contain"
                ></video>
              </div>
            </div>
            <div>
              <p class="text-sm mb-2 mt-3 !font-title leading-none sm:heading-6 sm:mb-[1%] sm:mt-[2%]">
                {{ work.category }}
              </p>
              <div class="items-start justify-between sm:flex sm:items-center">
                <h3 class="heading-4 !font-title mb-3 font-bold uppercase sm:heading-3 sm:mb-0">
                  {{ work.name }}
                </h3>
                <div class="flex flex-wrap select-none gap-1.5">
                  <p
                    class="rounded-full border border-flax-smoke-300 px-3 py-1.5 text-xs transition-[background-color,color] duration-500 ease-in-out hover:bg-flax-smoke-300 hover:text-flax-smoke-900 sm:px-4 sm:py-2 sm:text-sm"
                    v-for="tag in work.tags"
                    :key="tag"
                  >
                    <span>{{ tag }}</span>
                  </p>
                  <p
                    class="rounded-full border border-flax-smoke-300 bg-flax-smoke-300 px-3 py-1.5 text-xs text-flax-smoke-900 transition-[background-color,color] duration-500 ease-in-out hover:bg-transparent hover:text-flax-smoke-300 sm:px-4 sm:py-2 sm:text-sm"
                  >
                    <span>{{ work.year }}</span>
                  </p>
                </div>
              </div>
            </div>
          </a>
          
          <div v-if="work.details" class="mt-3 sm:mt-4">
            <button
              @click.stop="toggleDetails(i)"
              class="flex items-center gap-2 rounded-lg border border-flax-smoke-500/30 bg-flax-smoke-950/50 px-3 py-1.5 text-xs font-medium text-flax-smoke-300 transition-all duration-300 hover:border-flax-smoke-400/50 hover:bg-flax-smoke-900/50 sm:px-4 sm:py-2 sm:text-sm"
            >
              <span>{{ expandedProjects[i] ? 'Hide Details' : 'Show Details' }}</span>
              <svg
                :class="{ 'rotate-180': expandedProjects[i] }"
                class="h-3 w-3 transition-transform duration-300 sm:h-4 sm:w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div
              v-show="expandedProjects[i]"
              class="mt-2 overflow-hidden rounded-lg border border-flax-smoke-500/30 bg-flax-smoke-950/30 p-3 backdrop-blur-sm sm:mt-3 sm:p-4"
            >
              <ul class="space-y-2 text-xs text-flax-smoke-300 sm:text-sm">
                <li
                  v-for="(detail, idx) in work.details"
                  :key="idx"
                  class="flex gap-2 sm:gap-3"
                >
                  <span class="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-flax-smoke-400"></span>
                  <p class="leading-relaxed">{{ detail }}</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { computed, onBeforeMount, onMounted, ref } from 'vue';
  import gsap from 'gsap';
  import { workBg1, workBg2, workBg3, workBg4,workBg5,workBg6 } from '@/assets/images';
  import { useWindowSize } from '@vueuse/core';

  const isSmallScreen = computed(() => {
    return useWindowSize().width.value < 768;
  });
  const index = ref(0);
  const selectedWorks = ref('Selected Projects /');

  const tl = gsap
    .timeline({ defaults: { duration: 0.25 } })
    .to(['#cursor', '#inner'], {
      scale: 1,
      opacity: 1,
    })
    .paused(true);

  const showCursor = () => {
    tl.play();
  };
  const hideCursor = () => {
    tl.reverse();
  };

  const expandedProjects = ref<Record<number, boolean>>({});

  const playClickSound = async () => {
    const { playSound } = await import('@/lib/sound-engine');
    const { uChatScrollButtonSound } = await import('@/lib/sounds/u-chat-scroll-button');
    await playSound(uChatScrollButtonSound.dataUri, { volume: 0.4 });
  };

  const toggleDetails = (index: number) => {
    expandedProjects.value[index] = !expandedProjects.value[index];
    playClickSound();
  };

  const selectedWorksProps = [
    {
      id: 0,
      name: 'TeaL;DR',
      category: 'AI-Powered Discord Bot & Graph RAG',
      tags: ['Python', 'Discord.py', 'Neo4j', 'GCP'],
      videoSrc: 'https://dimpl.vercel.app/work10.mp4', 
      imageBg: workBg1,
      url: 'https://github.com/limo-git',
      year: '2026',
      details: [
        'Built an AI-powered Discord bot on GCP across 10+ servers, implementing a hybrid Graph RAG architecture combining Neo4j graph traversal with pgvector semantic search with real time indexing',
        'Engineered a Hybrid BM25 + Dense vector retrieval pipeline with Reciprocal Rank Fusion and intent-based routing across 7 query pipelines, covering expert finding, temporal recaps, and multi-turn conversation context',
        'Implemented confidence gating and Corrective RAG loops with source-constrained generation to eliminate hallucination.'
      ]
    },
    {
      id: 1,
      name: 'Doc.io',
      category: 'Documentation Platform & API Management',
      tags: ['TypeScript', 'Next.js', 'NextAuth', 'Neo4j'],
      videoSrc: 'https://dimpl.vercel.app/work5.mp4', 
      imageBg: workBg2,
      url: 'https://docio-cli-x1nt-1b61j4cui-limos-projects-27ec13f4.vercel.app/',
      year: '2024',
      details: [
        'Engineered Doc.io, a tool that converts text in markdown into a user-friendly documentation website.',
        'Implemented OpenAPI and Swagger UI for API references and ContentLayer for markdown conversion.',
        'Integrated NextAuth for authentication and utilized Neo4J and AWS s3 for data storage and management.'
      ]
    },
    {
      id: 2,
      name: 'CaptionCraft v2',
      category: 'AI Subtitle Generation & Video Processing',
      tags: ['TypeScript', 'Next.js', 'whisperX', 'CUDA'],
      videoSrc: 'https://dimpl.vercel.app/work6.mp4', 
      imageBg: workBg3,
      url: 'https://github.com/GitHoobar/cc',
      year: '2024',
      details: [
        'Conceptualized "Caption Craft", aimed at generating subtitles for local and YouTube videos.',
        'Integrated PyTube, Cloudinary for storage, and used ffmpeg for preprocessing.',
        'Used whisperX and CUDA for GPU-accelerated computational tasks related to audio processing.'
      ]
    },
    {
      id: 3,
      name: 'NSUT.AI',
      category: 'Full-Stack Web Application',
      tags: ['React.js', 'MongoDB', 'Express', 'Node.js'],
      videoSrc: 'https://dimpl.vercel.app/work8.mp4', 
      imageBg: workBg4,
      url: 'https://nsutai.vercel.app',
      year: '2024',
      details: [
        'Contributed in building a web app for NSUT.AI (the AI society of NSUT) to promote community engagement.',
        'Crafted an intuitive user interface, developed RESTful APIs ensuring rigorous testing with Postman.',
        'Utilized Clerk for secure user authentication via OAuth and used mongoDB to store user data.'
      ]
    },
    {
      id: 4,
      name: 'PDFosaurous',
      category: 'Interactive PDF Game Engine',
      tags: ['Python', 'PDF Internals', 'JavaScript'],
      videoSrc: 'https://dimpl.vercel.app/work9.mp4', 
      imageBg: workBg5,
      url: 'https://github.com/limo-git/PDFosaurus/blob/main/dinosaur_game.pdf',
      year: '2025',
      details: [
        'A fully interactive, self-contained T-Rex game embedded directly inside a PDF using low-level PDF syntax and embedded JavaScript.',
        'No external tools, no dependencies—just raw PDF internals powering an interactive experience inside Chrome web browser.',
        'Demonstrates deep understanding of PDF specification and creative use of embedded scripting capabilities.'
      ]
    },
    {
      id: 5,
      name: 'Portfolio',
      category: 'Frontend & Spline',
      tags: ['Next.js', 'Tailwind', 'Spline'],
      videoSrc: 'https://dimpl.vercel.app/work7.mp4', 
      imageBg: workBg6,
      url: 'https://github.com/limo-git/portfolio',
      year: '2024',
    },
    
   
  ];
const handleVideoError = (event: any) => {
  console.error('Video failed to load:', event);
  // You can add more details here to inspect the event object further
  console.log('Video URL:', event.target.src);
};

  // Reusable function to handle forward scroll animation
  const createForwardTimeline = (
    index: any,
    i: any,
    selectedWorksProps: any[],
  ) => {
    const tl = gsap.timeline({
      defaults: { duration: 0.3 },
    });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: 100,
      onComplete: () => {
        index.value = Math.min(i, selectedWorksProps.length - 1);
      },
    }).to('#index', {
      yPercent: 0,
      ease: 'power1.inOut',
    });

    return tl;
  };

  // Reusable function to handle backward scroll animation
  const createBackwardTimeline = (index: any, i: any) => {
    const tl = gsap.timeline({ defaults: { duration: 0.3 } });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: -100,
      onComplete: () => {
        index.value = Math.max(i, 0);
      },
    }).to('#index', {
      yPercent: 0,
      duration: 0.3,
      ease: 'power1.inOut',
    });

    return tl;
  };

const handleIntersection = (entries: IntersectionObserverEntry[]) => {
  entries.forEach((entry) => {
    const video = entry.target as HTMLVideoElement;
    if (entry.isIntersecting) {
      const src = video.getAttribute('data-src');
      if (src) {
        video.src = src;
        video.play().catch((err) => console.error('Video playback failed:', err));
      } else {
        console.error('Missing data-src for video:', video);
      }
    }
  });
};


  onBeforeMount(() => {
    selectedWorks.value = textSplitterIntoChar('Selected Works / ', true);
  });

  onMounted(() => {
    const workVideos = document.querySelectorAll('.work-video');

    // Create the IntersectionObserver
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.25, // Trigger when 25% of the video is visible
    });

    // Observe each video element
    workVideos.forEach((video) => {
      observer.observe(video);
    });

    animateSplitText(
      '#selectedWorks .letters',
      '#selected-works-text',
      0.7,
      0.01,
      0,
    );

    // Apply GSAP animations to each div
    if (!isSmallScreen.value)
      gsap.utils.toArray('.work-card').forEach((div: any, i: any) => {
        gsap.timeline({ defaults: { duration: 0.7 } }).to(div, {
          scrollTrigger: {
            trigger: div,
            // start: 'top 40%',
            start: 'top 25%',
            // end: 'bottom 40%',
            end: 'bottom 25%',
            scrub: 0.01,
            // markers: true,
            onLeaveBack: () => {
              // Backward scroll animation
              if (index.value !== 0) {
                gsap.to('#index', {
                  yPercent: 100,
                  duration: 0.3,
                  ease: 'power4.inOut',
                  onComplete: () => {
                    createBackwardTimeline(index, i - 1);
                  },
                });
              }
            },
          },
          ease: 'power1.inOut',
          onComplete: () => {
            // Forward scroll animation
            if (index.value !== selectedWorksProps.length - 1) {
              gsap.to('#index', {
                yPercent: -100,
                duration: 0.3,
                ease: 'power4.inOut',
                onComplete: () => {
                  createForwardTimeline(index, i + 1, selectedWorksProps);
                },
              });
            }
          },
        });
      });
  });
</script>
